import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Lock, Award, MapPin, Camera, ChevronRight, Plus, Target, Calendar, Sparkles } from "lucide-react";
import { api, mediaSrc } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useUI } from "@/context/UIContext";
import { SiteHeader } from "@/components/SiteHeader";
import { Footer } from "@/components/BusinessSection";

const overline = "hr-overline text-blaze text-[11px]";

function Stat({ value, label, sub }) {
  return (
    <div data-testid={`passport-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="font-serif text-4xl sm:text-5xl text-bone leading-none">{value}</div>
      <div className="text-bone/50 text-xs uppercase tracking-wider mt-2">{label}</div>
      {sub && <div className="text-bone/30 text-[11px] mt-0.5">{sub}</div>}
    </div>
  );
}

const RESULT_LABEL = { success: "Harvest", unfilled: "Unfilled", "": "Logged" };

export default function HuntPassport() {
  const { isAuthed, ready, user } = useAuth();
  const { setBuildHuntOpen } = useUI();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState({ species: "", state: "", result: "" });

  useEffect(() => {
    if (isAuthed) api.passport().then(setData).catch(() => setData(null));
  }, [isAuthed]);

  const hunts = data?.hunts || [];
  const filtered = useMemo(() => hunts.filter((h) =>
    (!filter.species || h.species === filter.species) &&
    (!filter.state || h.jurisdiction === filter.state) &&
    (!filter.result || (h.harvest_result || "") === filter.result)
  ), [hunts, filter]);

  const grouped = useMemo(() => {
    const g = {};
    filtered.forEach((h) => { const y = h.year || "Undated"; (g[y] = g[y] || []).push(h); });
    return Object.entries(g).sort((a, b) => (a[0] < b[0] ? 1 : -1));
  }, [filtered]);

  if (!ready || (isAuthed && !data)) {
    return <div className="bg-charcoal min-h-screen"><SiteHeader /><div className="py-32 grid place-items-center"><Loader2 className="animate-spin text-blaze" /></div></div>;
  }

  if (!isAuthed) {
    return (
      <div className="bg-charcoal min-h-screen"><SiteHeader />
        <div className="max-w-lg mx-auto text-center py-24 px-5">
          <Award className="text-blaze mx-auto" size={30} />
          <h1 className="font-serif text-4xl text-bone mt-5">Hunt Passport</h1>
          <p className="text-bone/60 mt-3">Sign in to open your lifetime hunting record.</p>
        </div>
      </div>
    );
  }

  const speciesOpts = [...new Set(hunts.map((h) => h.species).filter(Boolean))].sort();
  const stateOpts = [...new Set(hunts.map((h) => h.jurisdiction).filter(Boolean))].sort();

  return (
    <div className="bg-charcoal min-h-screen grain text-bone" data-testid="hunt-passport-page">
      <SiteHeader />
      <main className="max-w-5xl mx-auto px-5 sm:px-10 py-10">
        <span className={overline}>{user?.display_name}</span>
        <h1 className="font-serif text-4xl sm:text-6xl mt-2">Hunt Passport</h1>
        <p className="text-bone/50 mt-2">Every hunt — filled tag or not — is part of your story.</p>
        <div className="mt-3 flex items-center gap-4">
          <Link to="/trophy-room" className="inline-flex items-center gap-1.5 text-sm font-semibold text-blaze hover:gap-2.5 transition-all" data-testid="link-trophy-room"><Award size={15} /> Trophy Room</Link>
          <Link to="/year-in-review" className="inline-flex items-center gap-1.5 text-sm font-semibold text-blaze hover:gap-2.5 transition-all" data-testid="link-year-in-review"><Sparkles size={15} /> Year in Review</Link>
        </div>

        {/* Stats */}
        <div className="mt-8 grid grid-cols-2 sm:grid-cols-5 gap-6 bg-white/[0.03] border border-white/10 rounded-xl p-7">
          <Stat value={data.total_hunts} label="Hunts" />
          <Stat value={data.harvests} label="Harvests" />
          <Stat value={data.states_hunted} label="States" />
          <Stat value={data.species_hunted} label="Species" />
          <Stat value={data.days_known ? data.days_afield : "—"} label="Days afield" sub={data.days_known ? null : "when known"} />
        </div>

        {hunts.length === 0 ? (
          <div className="mt-10 text-center py-16 border border-dashed border-white/15 rounded-xl" data-testid="passport-empty">
            <p className="text-bone/60">No hunts logged yet. Add a hunt — even one from decades ago.</p>
            <button onClick={() => setBuildHuntOpen(true)} className="mt-5 inline-flex items-center gap-2 bg-blaze text-white px-6 py-3 rounded-full text-sm font-semibold hover:bg-blaze-light transition-colors" data-testid="passport-build-hunt"><Plus size={16} /> Build a hunt</button>
          </div>
        ) : (
          <>
            {/* Filters */}
            <div className="mt-8 flex flex-wrap gap-2.5" data-testid="passport-filters">
              <select value={filter.species} onChange={(e) => setFilter({ ...filter, species: e.target.value })} className="bg-white/5 border border-white/15 rounded px-3.5 py-2 text-sm text-bone outline-none focus:border-blaze" data-testid="filter-species">
                <option value="" className="bg-charcoal">All species</option>{speciesOpts.map((s) => <option key={s} value={s} className="bg-charcoal">{s}</option>)}
              </select>
              <select value={filter.state} onChange={(e) => setFilter({ ...filter, state: e.target.value })} className="bg-white/5 border border-white/15 rounded px-3.5 py-2 text-sm text-bone outline-none focus:border-blaze" data-testid="filter-state">
                <option value="" className="bg-charcoal">All states</option>{stateOpts.map((s) => <option key={s} value={s} className="bg-charcoal">{s}</option>)}
              </select>
              <select value={filter.result} onChange={(e) => setFilter({ ...filter, result: e.target.value })} className="bg-white/5 border border-white/15 rounded px-3.5 py-2 text-sm text-bone outline-none focus:border-blaze" data-testid="filter-result">
                <option value="" className="bg-charcoal">All results</option>
                <option value="success" className="bg-charcoal">Harvest</option>
                <option value="unfilled" className="bg-charcoal">Unfilled</option>
              </select>
            </div>

            {/* Timeline */}
            <div className="mt-8 space-y-10">
              {grouped.map(([year, list]) => (
                <div key={year}>
                  <div className="flex items-center gap-3 mb-4">
                    <span className="font-serif text-2xl text-bone">{year}</span>
                    <span className="text-bone/30 text-sm">{list.length} hunt{list.length === 1 ? "" : "s"}</span>
                    <div className="flex-1 h-px bg-white/10" />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {list.map((h) => (
                      <motion.button key={h.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                        onClick={() => navigate(`/hunts/${h.id}`)}
                        className="text-left bg-white/[0.04] border border-white/10 hover:border-blaze/50 rounded-xl overflow-hidden transition-colors group flex" data-testid={`passport-hunt-${h.id}`}>
                        <div className="w-24 sm:w-28 shrink-0 bg-white/5 grid place-items-center overflow-hidden">
                          {h.photo ? <img src={mediaSrc(h.photo)} alt={h.title} className="w-full h-full object-cover" /> : <Camera size={20} className="text-bone/20" />}
                        </div>
                        <div className="p-4 flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${h.harvest_result === "success" ? "bg-forest/30 text-forest" : "bg-white/10 text-bone/50"}`}>{RESULT_LABEL[h.harvest_result] || "Logged"}</span>
                            {h.photo_count > 0 && <span className="text-bone/30 text-[11px] flex items-center gap-0.5"><Camera size={11} />{h.photo_count}</span>}
                          </div>
                          <h3 className="font-semibold text-bone mt-1.5 truncate">{h.title}</h3>
                          <p className="text-bone/50 text-xs mt-1 flex items-center gap-1.5 flex-wrap">
                            <span className="flex items-center gap-1"><Target size={11} />{h.species}</span>
                            {h.jurisdiction && <span className="flex items-center gap-1"><MapPin size={11} />{h.jurisdiction}</span>}
                            {h.start_date && <span className="flex items-center gap-1"><Calendar size={11} />{h.start_date}{h.approximate ? " (approx)" : ""}</span>}
                          </p>
                          <span className="text-blaze text-xs font-semibold mt-2 inline-flex items-center gap-0.5 group-hover:gap-1.5 transition-all">Open <ChevronRight size={13} /></span>
                        </div>
                      </motion.button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
        <p className="text-bone/40 text-xs mt-10 flex items-center gap-1.5"><Lock size={11} /> Your hunts are private by default. You control what appears on your public profile.</p>
      </main>
      <Footer />
    </div>
  );
}
